/**
 * API 클라이언트 — 응답 공통 포맷 { code, message, data } 처리 + JWT 부착.
 * dev 는 vite proxy(/api → :3000). 토큰은 localStorage 보관(무상태 설계).
 */
const TOKEN_KEY = 'da.accessToken';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null): void {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

/**
 * 인증 만료(401) 통지 — 액세스 토큰이 만료·무효화되면 등록된 핸들러를 호출한다.
 * 로그인 시도의 401(자격증명 오류)과 구분하기 위해, **토큰을 지닌 요청**이 401 을 받은 경우에만 통지한다.
 */
type UnauthorizedHandler = () => void;
const unauthorizedHandlers = new Set<UnauthorizedHandler>();

export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
}

/** JWT 만료 시각(ms epoch) — 토큰의 exp 클레임. 해석 불가 시 null */
export function tokenExpiresAt(token = getToken()): number | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: number }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    public code: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown, extraHeaders?: Record<string, string>): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json', ...extraHeaders };
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const authenticated = !!token; // 로그인 요청(토큰 없음)의 401 과 구분

  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let json: { code: number; message: string; data: T };
  try {
    json = await res.json();
  } catch {
    throw new ApiError(res.status, `HTTP ${res.status}`);
  }
  if (!res.ok || json.code !== 0) {
    if (res.status === 401) {
      setToken(null);
      // 인증된 요청이 거부됨 = 세션 만료 → 앱 전체에 통지(명시적 로그아웃 처리)
      if (authenticated) for (const h of unauthorizedHandlers) h();
    }
    throw new ApiError(json.code ?? res.status, json.message ?? `HTTP ${res.status}`);
  }
  return json.data;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown, headers?: Record<string, string>) => request<T>('POST', p, b, headers),
  patch: <T>(p: string, b?: unknown) => request<T>('PATCH', p, b),
  del: <T>(p: string) => request<T>('DELETE', p),
};

/** 다단계 작업 식별자 — 진행 모달이 GET /operations/:opId 로 폴링할 때 쓴다 */
export function newOperationId(): string {
  return crypto.randomUUID();
}
